import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Play, Pause, Plus, Trash2, Scissors, SkipForward, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (s) => {
  if (!s || !isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const parseTime = (str) => {
  if (!str) return 0;
  const s = str.trim();
  if (/^\d+:\d{1,2}$/.test(s)) {
    const [m, sec] = s.split(':').map(Number);
    return m * 60 + sec;
  }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : Math.max(0, n);
};

const VideoEditorModal = ({ isOpen, onClose, video, projectId, onUpdate }) => {
  const videoRef = useRef(null);
  const isSeeking = useRef(false);

  const [duration, setDuration] = useState(0);
  const [loadingDuration, setLoadingDuration] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [cuts, setCuts] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [startInput, setStartInput] = useState('0');
  const [endInput, setEndInput] = useState('0');
  const [videoError, setVideoError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [lastFailedEditTaskId, setLastFailedEditTaskId] = useState(null);

  const cuttingPath = video?.path;
  const videoUrl = cuttingPath
    ? `${API}/uploads/${encodeURIComponent(cuttingPath.split('/').pop())}?v=${encodeURIComponent(cuttingPath)}`
    : null;

  // Fetch duration from backend (ffprobe — reliable for all formats)
  const fetchDuration = useCallback(async () => {
    if (!video?.id || !projectId) return;
    setLoadingDuration(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/videos/${video.id}/duration`);
      const d = res.data.duration;
      if (d > 0) {
        setDuration(d);
        setEndInput(String(Math.floor(d)));
      }
    } catch (err) {
      console.error('Failed to fetch duration:', err);
      // Fallback: try browser metadata
    }
    setLoadingDuration(false);
  }, [video?.id, projectId]);

  // Reset state when modal opens + fetch duration
  useEffect(() => {
    if (isOpen && video) {
      setCuts([]);
      setCurrentTime(0);
      setStartInput('0');
      setEndInput('0');
      setPreviewMode(false);
      setIsPlaying(false);
      setDuration(0);
      setVideoError(null);
      fetchDuration();
    }
  }, [isOpen, video, fetchDuration]);

  // Fallback: use browser metadata if backend didn't return duration
  const onLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration;
    if (d && isFinite(d) && d > 0 && d < 86400) { // sanity check: < 24 hours
      setDuration(prev => prev > 0 ? prev : d); // don't override backend value
      setEndInput(prev => prev === '0' ? String(Math.floor(d)) : prev);
    }
  }, []);

  const onTimeUpdate = useCallback(() => {
    if (isSeeking.current) return;
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime;

    if (previewMode && cuts.length > 0) {
      const sortedCuts = [...cuts].sort((a, b) => a.start - b.start);
      for (const cut of sortedCuts) {
        if (t >= cut.start && t < cut.end) {
          v.currentTime = cut.end;
          return;
        }
      }
    }
    setCurrentTime(t);
  }, [previewMode, cuts]);

  const onSliderInput = useCallback((e) => {
    isSeeking.current = true;
    setCurrentTime(parseFloat(e.target.value));
  }, []);

  const onSliderChange = useCallback((e) => {
    const t = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
    setTimeout(() => { isSeeking.current = false; }, 100);
  }, []);

  const onTimelineClick = useCallback((e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const t = ratio * duration;
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  }, [duration]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) v.pause(); else v.play();
  }, [isPlaying]);

  const addCut = useCallback(() => {
    const start = parseTime(startInput);
    const end = parseTime(endInput);
    if (start >= end) {
      toast.error('Start time must be before end time');
      return;
    }
    if (duration > 0 && end > duration + 0.5) {
      toast.error(`End time (${fmt(end)}) exceeds video duration (${fmt(duration)})`);
      return;
    }
    // Check overlap with existing cuts
    for (const c of cuts) {
      if (start < c.end && end > c.start) {
        toast.error(`This cut overlaps with existing cut ${fmt(c.start)} → ${fmt(c.end)}`);
        return;
      }
    }
    setCuts(prev => [...prev, { start, end }].sort((a, b) => a.start - b.start));
    toast.success(`Cut added: ${fmt(start)} → ${fmt(end)}`);
  }, [startInput, endInput, duration, cuts]);

  const removeCut = useCallback((i) => {
    setCuts(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  const seekTo = useCallback((t) => {
    if (videoRef.current && t >= 0) {
      videoRef.current.currentTime = t;
      setCurrentTime(t);
    }
  }, []);

  const [processingProgress, setProcessingProgress] = useState('');

  const applyCuts = useCallback(async () => {
    if (cuts.length === 0) {
      toast.error('Add at least one cut first');
      return;
    }
    setProcessing(true);
    setProcessingProgress('Starting video processing...');
    setEditError(null);
    try {
      const startRes = await axios.post(`${API}/projects/${projectId}/videos/${video.id}/edit`, { cuts });
      const taskId = startRes.data.task_id;
      if (!taskId) {
        toast.success('Video edited successfully!');
        onUpdate();
        onClose();
        setProcessing(false);
        setProcessingProgress('');
        return;
      }

      let editInterval = 3000;
      for (let i = 0; i < 200; i++) {
        await new Promise(r => setTimeout(r, editInterval));
        editInterval = Math.min(10000, Math.floor(editInterval * 1.3));
        try {
          const res = await axios.get(`${API}/edit-tasks/${taskId}`, { timeout: 15000 });
          const { status, error, result, progress } = res.data;
          if (progress) {
            setProcessingProgress(progress);
            editInterval = 3000; // Reset backoff on progress
          }
          if (status === 'completed') {
            toast.success(result?.message || 'Video edited successfully!');
            onUpdate();
            onClose();
            setProcessing(false);
            setProcessingProgress('');
            return;
          }
          if (status === 'failed') {
            setEditError(error || 'Video editing failed.');
            setLastFailedEditTaskId(taskId);
            toast.error(error || 'Video editing failed.');
            setProcessing(false);
            setProcessingProgress('');
            return;
          }
          // 'interrupted' = auto-resuming, keep polling
          if (status === 'interrupted') {
            setProcessingProgress('Auto-resuming after restart...');
          }
        } catch { /* network error, keep polling */ }
      }
      toast.error('Video editing timed out.');
      setEditError('Video editing timed out.');
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Failed to start video editing';
      toast.error(errMsg);
      setEditError(errMsg);
    }
    setProcessing(false);
    setProcessingProgress('');
  }, [cuts, projectId, video, onUpdate, onClose]);

  const retryEdit = useCallback(async () => {
    if (!lastFailedEditTaskId) return;
    setProcessing(true);
    setProcessingProgress('Retrying...');
    setEditError(null);
    try {
      const res = await axios.post(`${API}/retry-edit/${lastFailedEditTaskId}`);
      const taskId = res.data.task_id;
      setLastFailedEditTaskId(null);

      let editInterval = 3000;
      for (let i = 0; i < 200; i++) {
        await new Promise(r => setTimeout(r, editInterval));
        editInterval = Math.min(10000, Math.floor(editInterval * 1.3));
        try {
          const pollRes = await axios.get(`${API}/edit-tasks/${taskId}`, { timeout: 15000 });
          const { status, error, result, progress } = pollRes.data;
          if (progress) setProcessingProgress(progress);
          if (status === 'completed') {
            toast.success(result?.message || 'Video edited successfully!');
            onUpdate();
            onClose();
            setProcessing(false);
            setProcessingProgress('');
            return;
          }
          if (status === 'failed') {
            setEditError(error || 'Retry failed.');
            toast.error(error || 'Retry failed.');
            setProcessing(false);
            setProcessingProgress('');
            return;
          }
        } catch { /* keep polling */ }
      }
    } catch (e) {
      toast.error('Retry failed.');
      setEditError('Retry request failed.');
    }
    setProcessing(false);
    setProcessingProgress('');
  }, [lastFailedEditTaskId, onUpdate, onClose]);

  if (!isOpen) return null;

  const timelineReady = duration > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="video-editor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="w-5 h-5" />
            Video Editor — Cut Sections
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video Player */}
          <div className="bg-black rounded-lg overflow-hidden">
            {videoError ? (
              <div className="flex items-center justify-center h-[280px] text-white text-center p-4">
                <div>
                  <p className="text-sm opacity-75">Video format unsupported or file corrupt.</p>
                </div>
              </div>
            ) : (
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full max-h-[300px]"
                onLoadedMetadata={onLoadedMetadata}
                onDurationChange={onLoadedMetadata}
                onTimeUpdate={onTimeUpdate}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={() => setVideoError(true)}
                onClick={togglePlay}
                preload="metadata"
                data-testid="video-editor-player"
              />
            )}
          </div>

          {/* Transport Controls */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={togglePlay}
              disabled={!!videoError}
              data-testid="video-editor-play-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>

            <input
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={currentTime}
              onInput={onSliderInput}
              onChange={onSliderChange}
              className="flex-1 h-2 cursor-pointer accent-blue-600"
              disabled={!!videoError || !timelineReady}
              data-testid="video-editor-slider"
            />

            <span className="font-mono text-sm whitespace-nowrap text-slate-700" data-testid="video-editor-time">
              {fmt(currentTime)} / {loadingDuration ? '...' : fmt(duration)}
            </span>
          </div>

          {/* Visual Timeline with cut markers */}
          {timelineReady && (
            <div
              className="relative h-10 bg-slate-200 rounded-md cursor-pointer select-none overflow-hidden"
              onClick={onTimelineClick}
              data-testid="video-editor-timeline"
            >
              {/* Keep regions (green) */}
              {(() => {
                const sortedCuts = [...cuts].sort((a, b) => a.start - b.start);
                const keeps = [];
                let lastEnd = 0;
                for (const c of sortedCuts) {
                  if (c.start > lastEnd) keeps.push({ start: lastEnd, end: c.start });
                  lastEnd = c.end;
                }
                if (lastEnd < duration) keeps.push({ start: lastEnd, end: duration });
                return keeps.map((k, i) => (
                  <div
                    key={`keep-${i}`}
                    className="absolute h-full bg-emerald-300/50"
                    style={{
                      left: `${(k.start / duration) * 100}%`,
                      width: `${((k.end - k.start) / duration) * 100}%`,
                    }}
                  />
                ));
              })()}
              {/* Cut regions (red striped) */}
              {cuts.map((c, i) => (
                <div
                  key={i}
                  className="absolute h-full bg-red-400/70 border-x-2 border-red-600"
                  style={{
                    left: `${(c.start / duration) * 100}%`,
                    width: `${Math.max(((c.end - c.start) / duration) * 100, 0.5)}%`,
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 6px)',
                  }}
                  data-testid={`timeline-cut-region-${i}`}
                >
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-red-900 truncate px-1">
                    {((c.end - c.start) >= 5) ? `${fmt(c.start)}–${fmt(c.end)}` : ((c.end - c.start) >= 2) ? `CUT` : ''}
                  </span>
                </div>
              ))}
              {/* Playhead line */}
              <div
                className="absolute top-0 h-full w-0.5 bg-blue-700 z-10 pointer-events-none"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
              {/* Playhead knob */}
              <div
                className="absolute w-3.5 h-3.5 bg-blue-600 rounded-full z-10 pointer-events-none border-2 border-white shadow"
                style={{
                  left: `${(currentTime / duration) * 100}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />
              {/* Time labels */}
              <span className="absolute bottom-0.5 left-1 text-[9px] text-slate-500 pointer-events-none">0:00</span>
              <span className="absolute bottom-0.5 right-1 text-[9px] text-slate-500 pointer-events-none">{fmt(duration)}</span>
            </div>
          )}

          {loadingDuration && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Detecting video duration...
            </div>
          )}

          {/* Cut Summary */}
          {cuts.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 bg-amber-50 rounded-lg p-2 border border-amber-200" data-testid="cut-summary">
              <span className="font-medium text-amber-700">Cuts Summary:</span>
              <span>Removing {cuts.reduce((t, c) => t + (c.end - c.start), 0).toFixed(1)}s</span>
              <span className="text-slate-400">|</span>
              <span>Result: {fmt(duration - cuts.reduce((t, c) => t + (c.end - c.start), 0))} (from {fmt(duration)})</span>
              <span className="text-slate-400">|</span>
              <span>{cuts.length} cut{cuts.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Preview Mode */}
          {cuts.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2">
                <SkipForward className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  Preview with Cuts {previewMode && <span className="text-green-600">(Active — cuts will be skipped)</span>}
                </span>
              </div>
              <Button
                variant={previewMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode(!previewMode)}
                data-testid="preview-mode-btn"
              >
                {previewMode ? 'Exit Preview' : 'Preview'}
              </Button>
            </div>
          )}

          {/* Cut Definition */}
          <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
            <h4 className="font-semibold text-sm text-slate-800">Define Cut (section to remove)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start (seconds or M:SS)</label>
                <div className="flex gap-2">
                  <Input
                    value={startInput}
                    onChange={(e) => setStartInput(e.target.value)}
                    placeholder="0"
                    className="font-mono"
                    data-testid="cut-start-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStartInput(String(Math.round(currentTime * 10) / 10))}
                    className="whitespace-nowrap text-xs"
                    data-testid="cut-start-use-current"
                  >
                    Use Current
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">End (seconds or M:SS)</label>
                <div className="flex gap-2">
                  <Input
                    value={endInput}
                    onChange={(e) => setEndInput(e.target.value)}
                    placeholder="10"
                    className="font-mono"
                    data-testid="cut-end-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEndInput(String(Math.round(currentTime * 10) / 10))}
                    className="whitespace-nowrap text-xs"
                    data-testid="cut-end-use-current"
                  >
                    Use Current
                  </Button>
                </div>
              </div>
            </div>
            <Button
              onClick={addCut}
              className="w-full"
              variant="secondary"
              disabled={!timelineReady}
              data-testid="add-cut-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Cut ({fmt(parseTime(startInput))} → {fmt(parseTime(endInput))})
            </Button>
          </div>

          {/* Cuts List */}
          {cuts.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-slate-800">
                Cuts to Apply ({cuts.length})
              </h4>
              {cuts.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200"
                  data-testid={`cut-item-${i}`}
                >
                  <span className="text-sm font-mono">
                    Cut {i + 1}: {fmt(c.start)} → {fmt(c.end)}{' '}
                    <span className="text-red-600">({Math.round(c.end - c.start)}s removed)</span>
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => seekTo(c.start)}
                      className="text-xs"
                      data-testid={`cut-jump-${i}`}
                    >
                      Jump
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCut(i)}
                      className="text-red-600 hover:text-red-800"
                      data-testid={`cut-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2 pt-3 border-t">
            {processing && processingProgress && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-700" data-testid="edit-progress">
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                <span>{processingProgress}</span>
              </div>
            )}
            {editError && !processing && (
              <div className="flex items-center gap-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="edit-error-panel">
                <span className="text-xs text-red-700 flex-1">{editError}</span>
                {lastFailedEditTaskId && (
                  <Button
                    onClick={retryEdit}
                    size="sm"
                    variant="destructive"
                    data-testid="retry-edit-btn"
                  >
                    Retry
                  </Button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={processing} data-testid="video-editor-cancel">
                Cancel
              </Button>
              <Button
                onClick={applyCuts}
                disabled={processing || cuts.length === 0}
                data-testid="video-editor-apply"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Scissors className="w-4 h-4 mr-2" />
                    Apply Cuts & Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEditorModal;